import { useIntl } from 'react-intl';
import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import StatusBadge from 'src/componentes/StatusBadge';
import { ENTIDADES_CUSTOMIZADAS, FILTROS, STATUS_DOCUMENTO } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import useAuth from 'src/hooks/useAuth';
import Search from 'src/paginas/GestaoDocumentos/Documento/Search';
import SearchSchema from 'src/paginas/GestaoDocumentos/Documento/SearchSchema';
import { Folder } from 'lucide-react';

function DocumentoAssociadoListLookup({ ...rest }) {
  const intl = useIntl();
  const { terms: resources } = useAuth();

  const getValue = (item, keys = []) => {
    if (!item) return null;

    const value = keys.find((key) => item?.[key] != null);
    return value ? item[value] : null;
  };

  const getPodeAssociar = (item) => {
    if (typeof item?.podeAssociar === 'boolean') return item.podeAssociar;
    return !item?.agrupador;
  };

  const renderNome = (item) => (
    <span className="font-weight-bold text-primary">
      {item?.agrupador && <Folder className="p-0 m-0" size={16} />} {item?.nome}
    </span>
  );

  const renderCodigo = (item) => getValue(item, ['idIntegracao']) || '-';

  const renderTipoDocumento = (item) => {
    const tipoDocumento = getValue(item, ['tipoDocumento']);

    if (!tipoDocumento) return '-';
    if (typeof tipoDocumento === 'string') return tipoDocumento;

    return tipoDocumento?.nome || tipoDocumento?.descricao || '-';
  };

  const renderResponsaveis = (item) => {
    const responsaveis = getValue(item, ['responsaveis']);

    if (!responsaveis) return '-';

    if (Array.isArray(responsaveis)) {
      return responsaveis
        .map((responsavel) => responsavel?.nome || responsavel)
        .filter(Boolean)
        .join(', ');
    }

    return responsaveis;
  };

  const renderDataVigencia = (item) => getValue(item, ['dataVigencia']) || '-';

  const renderStatus = (item) => {
    const status = getValue(item, ['status']);

    if (!status) return '-';
    if (
      status?.id !== STATUS_DOCUMENTO.VIGENTE &&
      status?.id !== STATUS_DOCUMENTO.VENCIDO
    )
      return '-';

    if (status?.cor || status?.corFundo) {
      return (
        <StatusBadge
          text={status?.nome || '-'}
          textColor={status?.corFundo ? status?.cor : undefined}
          backgroundColor={status?.corFundo || status?.cor}
        />
      );
    }

    return status?.nome || status;
  };

  const handleDisableSelectedItem = (item) => {
    return !getPodeAssociar(item);
  };

  const hintDisabledItem = () =>
    intl.formatMessage({
      id: 'labelSomenteDocumentosFinaisPodemSerAssociados',
    });

  return (
    <List
      url="/Documento"
      customAction="ObterListDocumentosAssociar"
      showSearch={true}
      searchComponent={<Search />}
      searchSchema={SearchSchema}
      pageSize={10}
      showMenu={false}
      showNew={false}
      showExport={false}
      filtroId={FILTROS.DOCUMENTO}
      showPage={true}
      showHierarchyPagination={true}
      showModal={true}
      modalClose={true}
      hierarchy={true}
      showCustomFields={true}
      tipoItem={ENTIDADES_CUSTOMIZADAS.GESTAO_DOCUMENTOS}
      disableSelectedItems={handleDisableSelectedItem}
      hintOnDisabledItem={hintDisabledItem}
      {...rest}
    >
      <ListColumn
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'codigo' }))}
        valueField="idIntegracao"
        valueFunction={renderCodigo}
        minWidth={120}
        default
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'labelTipoDocumento' })
        )}
        valueField="tipoDocumento"
        valueFunction={renderTipoDocumento}
        minWidth={150}
        default
      />
      <ListColumn
        tree={true}
        headerText={capitalizeFirstLetter(
          intl.formatMessage({
            id: 'documento',
          })
        )}
        valueField="nome"
        valueFunction={renderNome}
        minWidth={220}
        default
      />
      <ListColumn
        headerText={capitalizeFirstLetter(resources.responsaveis)}
        valueField="responsaveis"
        valueFunction={renderResponsaveis}
        minWidth={170}
        default
      />
      <ListColumn
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'dataVigencia' })
        )}
        valueField="dataVigencia"
        valueFunction={renderDataVigencia}
        minWidth={150}
        default
      />
      <ListColumn
        headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'labelSituacao' }))}
        valueField="status"
        valueFunction={renderStatus}
        minWidth={140}
        default
      />
    </List>
  );
}

export default DocumentoAssociadoListLookup;
